// src/companyBuckets.js
// Single source of truth for company-type buckets, used by:
//   - User Management (auth.jsx) -- restrict which buckets each rep can see
//   - App.jsx visibleCompanies filter -- apply that restriction app-wide
//   - Route Planner (route.jsx) -- per-route bucket filter (optional)
//
// Each bucket maps friendly labels to the raw HubSpot `properties.type`
// values that fall in that bucket. Matching is case-insensitive and
// whitespace-trimmed.
//
// Bucket order in this array = order they're tested in bucketForCompany().
// Order matters: more specific buckets must be listed before "other" since
// "other" is the catch-all (rawTypes: null).

export const COMPANY_BUCKETS = [
  {
    id: "mattress",
    label: "Mattress",
    rawTypes: ["gribetz/porter", "gribetz/porter prospect", "aac", "aac prospect"],
    // exact-match list (case-insensitive)
  },
  {
    id: "industrial",
    label: "Industrial",
    rawTypes: ["ind customer", "prospect"],
  },
  {
    id: "homequilting",
    label: "Home Quilting",
    // No exact-match list -- HubSpot type values for these vary (e.g.
    // "NECCHI", "INNOVA Customer", "Accustitch Prospect"). Instead match
    // by keyword substring. If your data has a more specific exact-match
    // list, swap rawTypes back in and remove keywords.
    rawTypes: null,
    keywords: ["necchi", "innova", "accustitch"],
  },
  {
    id: "other",
    label: "Other",
    rawTypes: null,  // catch-all for anything not matched above
  },
];

// Return the bucket id that a company belongs to based on its
// `properties.type`. Tested in COMPANY_BUCKETS order; first match wins.
// "other" always matches as the last fallback.
export function bucketForCompany(company) {
  const raw = (company?.properties?.type || "").trim().toLowerCase();
  for (const b of COMPANY_BUCKETS) {
    if (b.id === "other") continue;  // skip catch-all in matching loop
    // Exact-match list
    if (Array.isArray(b.rawTypes) && b.rawTypes.includes(raw)) return b.id;
    // Keyword substring match (case-insensitive against the raw type value)
    if (Array.isArray(b.keywords) && b.keywords.some(k => raw.includes(k))) return b.id;
  }
  return "other";
}

// Default allowedTypes for new users: full access to all buckets.
// Admins always bypass the filter via App.jsx logic regardless of this list.
export const DEFAULT_ALLOWED_TYPES = COMPANY_BUCKETS.map(b => b.id);

// ── Per-user pipeline visibility ─────────────────────────────────────────────
// Independent from COMPANY_BUCKETS above. The Pipeline tab uses these to gate
// which pipeline tabs each rep can see + which deals are visible. New users
// default to ["mattress","abm"] -- the two pipelines reps have historically
// worked. Admin must explicitly grant additional pipeline access in User
// Management.
//
// `id` matches the bucket string returned by classifyPipelineByHubSpotId()
// in hubspot.js. Keep these in sync if pipeline IDs ever change.
export const SALES_PIPELINES = [
  { id: "mattress",          label: "Mattress" },
  { id: "abm",               label: "ABM CNC/Quilter" },
  { id: "aac",               label: "AAC" },
  { id: "necchi",            label: "NECCHI" },
  { id: "newdealer_necchi",  label: "NECCHI Dealer Signup" },
  { id: "newdealer_innova",  label: "INNOVA Dealer Signup" },
  { id: "dealerlead",        label: "Dealer Lead" },
];

// Default pipeline access for new users: Mattress + ABM CNC only. Admin grants
// other pipelines explicitly. This is intentionally conservative -- it's
// safer to start a new rep with limited access and expand as needed than to
// expose them to data they shouldn't see.
export const DEFAULT_ALLOWED_PIPELINES = ["mattress", "abm"];


